import type { OnDestroy } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, type OnInit } from '@angular/core';
import { PreviewType } from '@components/sp-file-manager/src/types';
import { type SelectedFileExtended } from '@common/types/file.type';
import { type SelectedFileInterface } from '@sendpulse/file-manager';

import type { ProductCardWidgetData } from '@web/widgets/product-card/type';
import { type ProductCardFormType } from '@web/widgets/product-card/type';
import { imageFormControl } from '@web/widgets/product-card/form/product-card-widget.form';
import { extendWebFileDataPromise } from '@common/helpers';
import { type SortableEvent, type SortableOptions } from 'sortablejs';
import { SiteFacade } from '@web/store/website/site/site.facade';
import { environment } from '@web-env/environment';
import { TranslateModule } from '@ngx-translate/core';
import { SortablejsModule } from '@moddi3/ngx-sortablejs';
import { buildFileSrcSets, formatFileUrl, formatFileUrlWithResize } from '@components/sp-file-manager/src/helpers';
import { SpFileManagerComponentModule } from '@components/sp-file-manager/src/sp-file-manager.module';
import { CommonModule } from '@angular/common';
import { WidgetsDataTypes } from '@common/enums';
import { UseUnsplash } from '@web/shared/hooks/use-unsplash.hook';
import WidgetsLegacyActions from '@web/store/page-structure/widgets/widgets.actions';
import { Subject, takeUntil } from 'rxjs';
import { Actions, ofType } from '@ngrx/effects';
import { filter } from 'rxjs/operators';
import { SppFallbackImgDirective } from '@common/directives/fallback-img.directive';

@Component({
    selector: 'web-product-images',
    templateUrl: './product-images.component.html',
    styleUrls: ['./product-images.component.less'],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [CommonModule, SpFileManagerComponentModule, SortablejsModule, TranslateModule, SppFallbackImgDirective],
})
export class ProductImagesComponent implements OnInit, OnDestroy {
    @Input() images: ProductCardFormType['images'];
    @Input() widgetData: ProductCardWidgetData;
    @Input() widgetId: string;

    private readonly actions$ = inject(Actions);
    protected readonly websiteFacade = inject(SiteFacade);
    protected readonly changeDetectorRef = inject(ChangeDetectorRef);

    private readonly destroy$ = new Subject<void>();

    private readonly useUnsplash: UseUnsplash = inject(UseUnsplash);

    public sortableImages: SelectedFileExtended[] = [];
    public sortableOptions: SortableOptions = {
        onUpdate: (event) => {
            this.drop(event);
        },
    };

    public buttonDisabled: boolean = false;

    ngOnInit() {
        this.sortableImages = [...(this.widgetData?.element?.images || [])];
        this.unsplashImageSubscription();
    }

    public formatImageURL(image: SelectedFileExtended): string {
        return formatFileUrl(image, environment.fileManagerCdnUrl);
    }

    public formatImageURLWithResize(image: SelectedFileExtended): string {
        return formatFileUrlWithResize(image, environment.fileManagerCdnUrl, 720);
    }

    public async addImage(files: SelectedFileInterface[]): Promise<void> {
        if (!files) return;
        this.buttonDisabled = true;

        for (const file of files) {
            const extendedFile: SelectedFileExtended = await extendWebFileDataPromise(
                file,
                environment.fileManagerCdnUrl
            );

            const srcSets = buildFileSrcSets(extendedFile, environment.fileManagerCdnUrl);

            this.images.push(imageFormControl({ ...extendedFile, srcSets }));

            this.websiteFacade.resizeImage({
                path: extendedFile.path,
                url: `${environment.fileManagerCdnUrl}${extendedFile.url}`,
            });
        }

        this.buttonDisabled = false;
        this.changeDetectorRef.detectChanges();
    }

    public setDefaultImage(index: number): void {
        this.images.controls.forEach((image, i) => {
            const equals = index === i;
            if (equals) {
                image.patchValue({ ...image.value, isDefault: true });
            } else {
                image.patchValue({ ...image.value, isDefault: false });
            }
        });

        this.images.controls.sort((a, b) => {
            if (a.value.isDefault && !b.value.isDefault) {
                return -1;
            }

            if (!a.value.isDefault && b.value.isDefault) {
                return 1;
            }

            return 0;
        });
    }

    public deleteImage(index: number): void {
        this.images.removeAt(index);
    }

    public drop(event: SortableEvent): void {
        this.images.setValue(this.sortableImages);
    }

    openUnsplashModal(): void {
        this.useUnsplash.openUnsplashModal({ widgetId: this.widgetId, dataType: WidgetsDataTypes.product });
    }

    unsplashImageSubscription() {
        this.actions$
            .pipe(
                ofType(WidgetsLegacyActions.updateProductImageData),
                filter(({ id }) => id === this.widgetId),
                takeUntil(this.destroy$)
            )
            .subscribe(({ image }) => {
                this.images.push(imageFormControl(image));
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    protected readonly PreviewType = PreviewType;
}
